(() => {
  const instanceKey = Symbol.for('store-builder.navigation');

  if (window[instanceKey]) {
    window[instanceKey].initialize(document);
    return;
  }

  const drawerSelector = 'details[data-navigation-drawer]';
  const disclosureSelector = 'details[data-navigation-disclosure]';
  const desktopNavigation = window.matchMedia('(min-width: 64rem)');
  const focusableSelector = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'summary',
    '[tabindex]:not([tabindex="-1"])',
  ].join(',');
  const documentElement = document.documentElement;
  const drawers = new Map();
  const disclosures = new Map();
  const lockedDrawers = new Set();
  let lockState;

  documentElement.classList.add('navigation-enhanced');
  documentElement.setAttribute('data-navigation-enhanced', '');

  const isFocusable = (element) =>
    !element.closest('[hidden], [inert]') &&
    !element.matches(':disabled') &&
    element.getAttribute('aria-hidden') !== 'true' &&
    element.getClientRects().length > 0;

  const getFocusableElements = (container) =>
    Array.from(container.querySelectorAll(focusableSelector)).filter(isFocusable);

  const lockPage = (drawer) => {
    if (lockedDrawers.has(drawer)) return;

    if (lockedDrawers.size === 0) {
      lockState = {
        hadClass: documentElement.classList.contains('navigation-open'),
        hadAttribute: documentElement.hasAttribute('data-navigation-open'),
        attributeValue: documentElement.getAttribute('data-navigation-open'),
      };
    }

    lockedDrawers.add(drawer);
    documentElement.classList.add('navigation-open');
    documentElement.setAttribute('data-navigation-open', '');
  };

  const unlockPage = (drawer) => {
    lockedDrawers.delete(drawer);
    if (lockedDrawers.size > 0 || !lockState) return;

    documentElement.classList.toggle('navigation-open', lockState.hadClass);

    if (lockState.hadAttribute) {
      documentElement.setAttribute('data-navigation-open', lockState.attributeValue ?? '');
    } else {
      documentElement.removeAttribute('data-navigation-open');
    }

    lockState = undefined;
  };

  const focusPanel = (panel) => {
    const firstFocusable = getFocusableElements(panel)[0];

    if (firstFocusable) {
      firstFocusable.focus({ preventScroll: true });
      return;
    }

    const previousTabIndex = panel.getAttribute('tabindex');
    panel.setAttribute('tabindex', '-1');
    panel.focus({ preventScroll: true });
    panel.addEventListener(
      'blur',
      () => {
        if (previousTabIndex === null) panel.removeAttribute('tabindex');
        else panel.setAttribute('tabindex', previousTabIndex);
      },
      { once: true },
    );
  };

  const createDrawer = (drawer) => {
    const trigger = drawer.querySelector(':scope > summary[data-navigation-trigger]');
    const panel = drawer.querySelector('[data-navigation-panel]');
    const header = drawer.closest('[data-header]');
    const focusFallback = header?.querySelector('[data-navigation-fallback-focus]');

    if (!trigger || !panel) return null;

    const closeButtons = Array.from(drawer.querySelectorAll('[data-navigation-close]'));
    let enhancedOpen = false;
    let focusFrame;
    let returnFocusOnToggle = false;

    const syncOpenState = () => {
      if (drawer.open === enhancedOpen) return;

      enhancedOpen = drawer.open;
      if (enhancedOpen) {
        drawers.forEach((otherController, otherDrawer) => {
          if (otherDrawer !== drawer && otherDrawer.open) otherController.close(false);
        });
        lockPage(drawer);
        focusFrame = window.requestAnimationFrame(() => {
          if (drawer.open && drawer.isConnected) focusPanel(panel);
        });
      } else {
        if (focusFrame) window.cancelAnimationFrame(focusFrame);
        unlockPage(drawer);
        if (returnFocusOnToggle && trigger.isConnected) trigger.focus({ preventScroll: true });
        returnFocusOnToggle = false;
      }
    };

    const close = (returnFocus = true) => {
      if (!drawer.open) return;
      returnFocusOnToggle = returnFocus;
      drawer.open = false;
      syncOpenState();
    };

    const onToggle = () => syncOpenState();
    const onTriggerClick = () => {
      if (drawer.open) returnFocusOnToggle = true;
    };
    const onCloseClick = () => close(true);
    const onBreakpointChange = (event) => {
      if (!event.matches || !drawer.open) return;

      close(false);
      if (focusFallback?.isConnected) focusFallback.focus({ preventScroll: true });
    };
    const onDrawerKeydown = (event) => {
      if (!drawer.open) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        event.stopPropagation();
        close(true);
        return;
      }

      if (event.key !== 'Tab') return;

      const focusableElements = getFocusableElements(panel);
      if (focusableElements.length === 0) {
        event.preventDefault();
        focusPanel(panel);
        return;
      }

      const firstFocusable = focusableElements[0];
      const lastFocusable = focusableElements[focusableElements.length - 1];

      if (event.shiftKey && document.activeElement === firstFocusable) {
        event.preventDefault();
        lastFocusable.focus();
      } else if (!event.shiftKey && document.activeElement === lastFocusable) {
        event.preventDefault();
        firstFocusable.focus();
      }
    };

    drawer.addEventListener('toggle', onToggle);
    drawer.addEventListener('keydown', onDrawerKeydown);
    trigger.addEventListener('click', onTriggerClick);
    desktopNavigation.addEventListener('change', onBreakpointChange);
    closeButtons.forEach((button) => button.addEventListener('click', onCloseClick));
    if (desktopNavigation.matches) close(false);
    syncOpenState();

    return {
      close,
      destroy() {
        if (focusFrame) window.cancelAnimationFrame(focusFrame);
        drawer.removeEventListener('toggle', onToggle);
        drawer.removeEventListener('keydown', onDrawerKeydown);
        trigger.removeEventListener('click', onTriggerClick);
        desktopNavigation.removeEventListener('change', onBreakpointChange);
        closeButtons.forEach((button) => button.removeEventListener('click', onCloseClick));
        enhancedOpen = false;
        unlockPage(drawer);
      },
    };
  };

  const createDisclosure = (disclosure) => {
    const trigger = disclosure.querySelector(':scope > summary');
    if (!trigger) return null;

    const onKeydown = (event) => {
      if (event.key !== 'Escape' || !disclosure.open) return;

      event.preventDefault();
      event.stopPropagation();
      disclosure.open = false;
      trigger.focus({ preventScroll: true });
    };

    disclosure.addEventListener('keydown', onKeydown);

    return {
      close() {
        disclosure.open = false;
      },
      destroy() {
        disclosure.removeEventListener('keydown', onKeydown);
      },
    };
  };

  const elementsWithin = (root, selector) => {
    const elements = [];
    if (root instanceof Element && root.matches(selector)) elements.push(root);
    root.querySelectorAll?.(selector).forEach((element) => elements.push(element));
    return elements;
  };

  const initialize = (root = document) => {
    elementsWithin(root, drawerSelector).forEach((drawer) => {
      if (drawers.has(drawer)) return;
      const controller = createDrawer(drawer);
      if (controller) drawers.set(drawer, controller);
    });

    elementsWithin(root, disclosureSelector).forEach((disclosure) => {
      if (disclosures.has(disclosure)) return;
      const controller = createDisclosure(disclosure);
      if (controller) disclosures.set(disclosure, controller);
    });
  };

  const destroyWithin = (root) => {
    elementsWithin(root, drawerSelector).forEach((drawer) => {
      drawers.get(drawer)?.destroy();
      drawers.delete(drawer);
    });

    elementsWithin(root, disclosureSelector).forEach((disclosure) => {
      disclosures.get(disclosure)?.destroy();
      disclosures.delete(disclosure);
    });
  };

  document.addEventListener('click', (event) => {
    disclosures.forEach((controller, disclosure) => {
      if (disclosure.open && !disclosure.contains(event.target)) controller.close();
    });
  });
  document.addEventListener('shopify:section:load', (event) => initialize(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyWithin(event.target));

  window[instanceKey] = { initialize };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initialize(document), { once: true });
  } else {
    initialize(document);
  }
})();
